import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCloneSet from 'hooks/useCloneSet';
import { useCurrentUser } from 'hooks/useCurrentUser';
import useStore from 'zstore';
import { GoalType } from 'models/set';
import { CONTENT_CREATOR_ROLES } from 'utils/constants';
import { getEditSetUrlForReactRouter } from 'utils/pathUtils';
import { AnalyticsService } from 'utils/AnalyticsService';

interface UseCopyContentOptions {
  courseId?: string;
  redirectOnSuccess?: boolean;
  onCopyStart?: () => void;
  onCopyComplete?: () => void;
  canEditContent?: boolean;
}

interface ContentItem {
  id: string;
  goalType: GoalType;
  learnVersion: number;
  state?: string;
}

export const useCopyContent = (options: UseCopyContentOptions = {}) => {
  const { courseId, redirectOnSuccess = false, onCopyStart, onCopyComplete, canEditContent } = options;
  const { t } = useTranslation();
  const history = useHistory();
  const user = useCurrentUser();
  const [cloneSet] = useCloneSet();
  const { makeToast } = useStore();
  const [copyingSetId, setCopyingSetId] = useState<string | null>(null);

  const userRole = user?.primaryPartner?.meta?.role;

  const canCopy = (set: ContentItem): boolean => {
    const isAllowedType = ['set', 'assessment', 'survey'].includes(set.goalType);
    const isNotBuilding = set.state !== 'building';
    const hasPermission = CONTENT_CREATOR_ROLES.includes(userRole);
    // If canEditContent is explicitly passed (course context), require it
    // Otherwise (content page), just check role
    const editPermission = canEditContent !== undefined ? canEditContent : true;
    return isAllowedType && isNotBuilding && hasPermission && editPermission;
  };

  const canCopyAsAssessment = (set: ContentItem): boolean => {
    return canCopy(set) && set.goalType === 'set' && set.learnVersion === 4;
  };

  const canCopyAsLearningSet = (set: ContentItem): boolean => {
    return canCopy(set) && set.goalType === 'assessment';
  };

  const getTypeLabel = (goalType: GoalType): string => {
    switch (goalType) {
      case 'assessment':
        return 'Assessment';
      case 'set':
        return 'Learning Set';
      case 'survey':
        return 'Survey';
      default:
        return 'Content';
    }
  };

  const handleCopy = async (set: ContentItem, targetType: GoalType, analyticsButtonType: string): Promise<void> => {
    AnalyticsService.getInstance().track('button_clicked', { button_type: analyticsButtonType });

    onCopyStart?.();
    setCopyingSetId(set.id);

    try {
      const payload: any = {
        data: {
          attributes: {
            goal_type: targetType,
            partner_id: user?.primaryPartner.id,
          },
        },
      };

      if (courseId) {
        payload.data.attributes.course_id = courseId;
      }

      const response = await cloneSet({ id: set.id, payload });
      const clonedSetId = response?.data?.target_set_id;

      // Check if the clone was successful
      if (!clonedSetId) {
        makeToast({ text: t('Failed to copy content. Please try again.'), isError: true });
        return;
      }

      if (redirectOnSuccess) {
        const typeLabel = getTypeLabel(targetType);
        makeToast({ text: t(`New ${typeLabel} created`), isError: false });
        history.push(getEditSetUrlForReactRouter(set.learnVersion, clonedSetId));
      }
    } catch (error) {
      console.error('Failed to copy content:', error);
      makeToast({ text: t('Failed to copy content. Please try again.'), isError: true });
    } finally {
      setCopyingSetId(null);
      onCopyComplete?.();
    }
  };

  return {
    canCopy,
    canCopyAsAssessment,
    canCopyAsLearningSet,
    handleCopy,
    copyingSetId,
    isCopying: copyingSetId !== null,
  };
};
